import React from 'react';
import { X, Pencil, Download, Mail } from 'lucide-react';
import EmailHistory from './EmailHistory';

const STATUS_COLORS = { impayee: 'bg-red-50 text-red-600', partielle: 'bg-amber-50 text-amber-600', payee: 'bg-green-50 text-green-600', annulee: 'bg-gray-100 text-gray-500' };
const STATUS_LABELS = { impayee: 'Impayée', partielle: 'Partielle', payee: 'Payée', annulee: 'Annulée' };

export default function InvoiceViewModal({ viewing, company, onClose, onEdit, onPDF, onEmail }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl w-full max-w-3xl my-8 shadow-2xl" data-testid="invoice-view-modal">
        {/* Document header */}
        <div className="bg-gradient-to-r from-[#DC2626] to-[#b91c1c] text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold tracking-wide">{company?.name || 'MCA CAR'}</h2>
              <p className="text-red-100 text-xs mt-1">{company?.address}, {company?.postal_code} {company?.city}</p>
              <p className="text-red-100 text-xs">Tél: {company?.phone} | {company?.email}</p>
              <p className="text-red-200 text-[10px] mt-1">SIREN: {company?.siren} — SIRET: {company?.siret} — TVA: {company?.tva_number}</p>
            </div>
            <div className="text-right">
              <span className="text-3xl font-black tracking-wider opacity-90">FACTURE</span>
              <p className="text-red-100 text-sm font-semibold mt-1">{viewing.number}</p>
            </div>
          </div>
        </div>

        {/* Meta bar */}
        <div className="bg-gray-50 border-b px-6 py-3 flex justify-between items-center text-xs">
          <div className="flex gap-6">
            <span className="text-gray-500">Date: <span className="text-gray-900 font-medium">{viewing.created_at?.slice(0, 10)}</span></span>
            {viewing.due_date && <span className="text-gray-500">Échéance: <span className="text-gray-900 font-medium">{viewing.due_date?.slice(0, 10)}</span></span>}
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full font-medium capitalize ${STATUS_COLORS[viewing.status] || 'bg-gray-100'}`}>{STATUS_LABELS[viewing.status] || viewing.status}</span>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-2"><X size={18} /></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Client + Vehicle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border rounded-lg p-4">
              <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-2 font-semibold">Destinataire</p>
              {viewing.client ? (
                <>
                  <p className="font-bold text-gray-900">{viewing.client.first_name} {viewing.client.last_name} {viewing.client.company_name}</p>
                  {viewing.client.address && <p className="text-sm text-gray-600 mt-1">{viewing.client.address}</p>}
                  {(viewing.client.postal_code || viewing.client.city) && <p className="text-sm text-gray-600">{viewing.client.postal_code} {viewing.client.city}</p>}
                  {viewing.client.phone && <p className="text-sm text-gray-500 mt-1">Tél: {viewing.client.phone}</p>}
                  {viewing.client.email && <p className="text-sm text-gray-500">{viewing.client.email}</p>}
                </>
              ) : <p className="text-sm text-gray-400 italic">Client supprimé</p>}
            </div>
            {viewing.vehicle && (
              <div className="border rounded-lg p-4">
                <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-2 font-semibold">Véhicule</p>
                <p className="font-bold text-gray-900">{viewing.vehicle.brand} {viewing.vehicle.model}</p>
                <p className="text-sm text-gray-600">Immat: <span className="font-mono font-semibold">{viewing.vehicle.plate}</span></p>
              </div>
            )}
          </div>

          {/* Items table */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#DC2626] text-white">
                  <th className="text-left p-3 text-xs font-semibold">Type</th>
                  <th className="text-left p-3 text-xs font-semibold">Désignation</th>
                  <th className="text-center p-3 text-xs font-semibold">Qté</th>
                  <th className="text-right p-3 text-xs font-semibold">PU HT</th>
                  <th className="text-center p-3 text-xs font-semibold">TVA</th>
                  <th className="text-right p-3 text-xs font-semibold">Total HT</th>
                </tr>
              </thead>
              <tbody>
                {viewing.items?.map((item, idx) => (
                  <tr key={`vitem-${idx}`} className={`border-t ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded font-medium ${item.type === 'service' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{item.type === 'service' ? 'M.O.' : 'Pièce'}</span></td>
                    <td className="p-3 font-medium text-gray-900">{item.description}</td>
                    <td className="p-3 text-center text-gray-600">{item.quantity}</td>
                    <td className="p-3 text-right text-gray-600">{item.unit_price?.toFixed(2)}€</td>
                    <td className="p-3 text-center text-gray-500">{item.tva_rate}%</td>
                    <td className="p-3 text-right font-semibold text-gray-900">{(item.quantity * item.unit_price).toFixed(2)}€</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals + Payments */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              {viewing.payments?.length > 0 && (
                <div className="border rounded-lg p-4">
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-3 font-semibold">Paiements enregistrés</p>
                  {viewing.payments.map((p, i) => (
                    <div key={`pay-${i}`} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0 text-sm">
                      <div>
                        <span className="capitalize font-medium">{p.method}</span>
                        {p.notes && <span className="text-gray-500 ml-2">— {p.notes}</span>}
                        <p className="text-xs text-gray-400">{p.date?.slice(0, 10)}</p>
                      </div>
                      <span className="font-semibold text-green-600">+{p.amount?.toFixed(2)}€</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border rounded-lg overflow-hidden">
              <div className="flex justify-between p-3 text-sm">
                <span className="text-gray-500">Total HT</span>
                <span className="font-medium">{viewing.subtotal?.toFixed(2)}€</span>
              </div>
              <div className="flex justify-between p-3 text-sm border-t">
                <span className="text-gray-500">TVA</span>
                <span className="font-medium">{viewing.tva_total?.toFixed(2)}€</span>
              </div>
              <div className="flex justify-between p-3 bg-[#DC2626] text-white text-lg font-bold">
                <span>Total TTC</span>
                <span>{viewing.total?.toFixed(2)}€</span>
              </div>
              {viewing.paid_amount > 0 && (
                <div className="flex justify-between p-3 text-sm text-green-600 bg-green-50">
                  <span className="font-medium">Payé</span>
                  <span className="font-bold">{viewing.paid_amount?.toFixed(2)}€</span>
                </div>
              )}
              {viewing.total - (viewing.paid_amount || 0) > 0.01 && (
                <div className="flex justify-between p-3 text-sm text-red-700 bg-red-50 font-bold">
                  <span>Reste à payer</span>
                  <span>{(viewing.total - (viewing.paid_amount || 0)).toFixed(2)}€</span>
                </div>
              )}
            </div>
          </div>

          {viewing.notes && (
            <div className="border-t pt-4">
              <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-1 font-semibold">Notes</p>
              <p className="text-sm text-gray-600">{viewing.notes}</p>
            </div>
          )}

          <EmailHistory history={viewing.email_history} />

          {/* Footer legal */}
          <div className="border-t pt-3 text-center">
            <p className="text-[10px] text-gray-400">{company?.name} — {company?.address}, {company?.postal_code} {company?.city}</p>
            <p className="text-[10px] text-gray-400">SIREN: {company?.siren} | SIRET: {company?.siret} | TVA: {company?.tva_number} | Tél: {company?.phone}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="p-5 border-t bg-gray-50 rounded-b-xl flex justify-between items-center">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg">Fermer</button>
          <div className="flex gap-2">
            <button onClick={onEmail} className="flex items-center gap-2 px-4 py-2 text-sm border bg-white rounded-lg hover:bg-blue-50 text-blue-700 font-medium" data-testid="view-invoice-email-btn"><Mail size={15} /> Envoyer par email</button>
            <button onClick={onPDF} className="flex items-center gap-2 px-4 py-2 text-sm border bg-white rounded-lg hover:bg-purple-50 text-purple-700 font-medium" data-testid="view-invoice-pdf-btn"><Download size={15} /> Voir / Télécharger PDF</button>
            <button onClick={onEdit} className="flex items-center gap-2 px-4 py-2 text-sm bg-[#DC2626] text-white rounded-lg hover:bg-red-700 font-medium"><Pencil size={15} /> Modifier</button>
          </div>
        </div>
      </div>
    </div>
  );
}
